import { BiConsumer, Consumer, Function } from "../../interfaces/functions";
import { Util } from "../../util/util";

export class DataTableColumn<T> {
  public name: string;
  public nameTranslateId: string;
  // Hides column for mobile devices
  public hideInCompactMode?: boolean;
  public idProvider: Function<T, string>;

  constructor(
      name: string,
      nameTranslateId: string,
      hideInCompactMode?: boolean,
      idProvider?: Function<T, string | null | undefined>) {
    this.name = name;
    this.nameTranslateId = nameTranslateId;
    this.hideInCompactMode = hideInCompactMode;
    this.idProvider = input => {
      if (idProvider) {
        return Util.safeString(idProvider(input));
      }
      return '';
    }
  }

  public isStringColumn(): boolean {
    return this instanceof StringColumn
        && !(this instanceof CompactTextColumn)
        && !(this instanceof CompactTextIfMobileColumn);
  }

  public asStringColumn(): StringColumn<T> {
    if (this instanceof StringColumn) {
      return this as StringColumn<T>;
    }
    throw new Error('Invalid column type');
  }

  public isCompactTextColumn(): boolean {
    return this instanceof CompactTextColumn
        && !(this instanceof CompactTextIfMobileColumn);
  }

  public asCompactTextColumn(): CompactTextColumn<T> {
    if (this instanceof CompactTextColumn) {
      return this as CompactTextColumn<T>;
    }
    throw new Error('Invalid column type');
  }

  public isCompactTextIfMobileColumn(): boolean {
    return this instanceof CompactTextIfMobileColumn;
  }

  public asCompactTextIfMobileColumn(): CompactTextIfMobileColumn<T> {
    if (this instanceof CompactTextIfMobileColumn) {
      return this as CompactTextIfMobileColumn<T>;
    }
    throw new Error('Invalid column type');
  }

  public isLinkColumn(): boolean {
    return this instanceof LinkColumn;
  }

  public asLinkColumn(): LinkColumn<T> {
    if (this instanceof LinkColumn) {
      return this as LinkColumn<T>;
    }
    throw new Error('Invalid column type');
  }

  public isCheckboxColumn(): boolean {
    return this instanceof CheckboxColumn;
  }

  public asCheckboxColumn(): CheckboxColumn<T> {
    if (this instanceof CheckboxColumn) {
      return this as CheckboxColumn<T>;
    }
    throw new Error('Invalid column type');
  }
}

export class StringColumn<T> extends DataTableColumn<T> {
  public valueExtractor: Function<T, string>;

  constructor(
      name: string,
      nameTranslateId: string,
      valueExtractor: Function<T, string | null | undefined>,
      hideInCompactMode?: boolean,
      idProvider?: Function<T, string | null | undefined>) {
    super(name, nameTranslateId, hideInCompactMode, idProvider);
    this.valueExtractor = input => valueExtractor(input) || '';
  }
}

export class CompactTextColumn<T> extends StringColumn<T> {
  public compactTextLength: number;

  constructor(
      name: string,
      nameTranslateId: string,
      valueExtractor: Function<T, string | null | undefined>,
      compactTextLength:number,
      hideInCompactMode?: boolean,
      idProvider?: Function<T, string | null | undefined>) {
    super(name, nameTranslateId, valueExtractor, hideInCompactMode, idProvider);
    this.compactTextLength = compactTextLength;
  }
}

export class CompactTextIfMobileColumn<T> extends CompactTextColumn<T> {

  constructor(
      name: string,
      nameTranslateId: string,
      valueExtractor: Function<T, string | null | undefined>,
      compactTextLength:number,
      hideInCompactMode?: boolean,
      idProvider?: Function<T, string | null | undefined>) {
    super(name, nameTranslateId, valueExtractor, compactTextLength, hideInCompactMode, idProvider);
  }
}

export class LinkColumn<T> extends DataTableColumn<T> {
  public urlExtractor: Function<T, string>;
  public textExtractor: Function<T, string>;

  constructor(
      name: string,
      nameTranslateId: string,
      urlExtractor: Function<T, string | null | undefined>,
      textExtractor: Function<T, string | null | undefined>,
      hideInCompactMode?: boolean,
      idProvider?: Function<T, string | null | undefined>) {
    super(name, nameTranslateId, hideInCompactMode, idProvider);
    this.urlExtractor = input => urlExtractor(input) || '';
    this.textExtractor = input => textExtractor(input) || '';
  }
}

export class CheckboxColumn<T> extends DataTableColumn<T> {
  public stateExtractor: Function<T, boolean>;
  public onStateChanged: BiConsumer<T, boolean>;

  constructor(
      name: string,
      nameTranslateId: string,
      stateExtractor: Function<T, boolean>,
      onStateChanged: BiConsumer<T, boolean>,
      hideInCompactMode?: boolean,
      idProvider?: Function<T, string | null | undefined>) {
    super(name, nameTranslateId, hideInCompactMode, idProvider);
    this.stateExtractor = stateExtractor;
    this.onStateChanged = onStateChanged;
  }
}
